import axios from "axios";
import * as CryptoJS from "crypto-js";
import { REACT_APP_BACKEND_URL, REACT_APP_TIME_URL } from "../../ytiruces";
import { useCustomToaster } from "../../hooks/useCustomToaster";
import { encrypt_5second, pyDecrypt, pyEncrypt } from "./encryptDec";

let cachedPathname = null;
let cachedDatetime = null;
let cachedDatetimePromise = null;
let lastFetchTime = 0;
const REFETCH_SECONDS = 1500;

const encDecConfig = {
    includes: ["all"],
    excludes: [],
};

function shouldEncryptDecrypt(url) {
    if (!url) return false;
    const { includes, excludes } = encDecConfig;

    const isExcludedAll = excludes.includes("all");
    const isIncludedAll = includes.includes("all");

    if (isExcludedAll) {
        return includes.some((path) => path !== "all" && url.includes(path));
    }
    if (isIncludedAll) {
        return !excludes.some((path) => path !== "all" && url.includes(path));
    }
    return includes.some((path) => url.includes(path));
}

function randomStringRefferal() {
    let result = '';
    const characters = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const charactersLength = characters.length;
    const randomValues = CryptoJS.lib.WordArray.random(100)
    for (let i = 0; i < 6; i++) {
        result += characters.charAt(randomValues.words[i] % charactersLength);
    }
    return result;
}

const DataService = () => {

    const toast = useCustomToaster();

    async function getGen(url) {
        const genT = randomStringRefferal()
        url = (url + '/' + genT).trim()
        const date = localStorage.getItem('emiTrevres')
        const ex_date = Number(date) + 10 // 5 seconds add
        const payload = {
            exp: ex_date,
            isHuman: true,
        };
        const token = signToken(payload, url)
        const encrtpted_genT = await encrypt_5second(genT)   // <-- await
        return { token, encrtpted_genT }
    }

    function signToken(payload, key) {
        let secret = key;
        let token = encodeToken(payload);
        let signature = CryptoJS.HmacSHA256(token, secret);
        signature = base64url(signature);
        return token + "." + signature;
    }

    function encodeToken(payload) {
        let header = { "alg": "HS256", "typ": "JWT" };
        let stringifiedHeader = CryptoJS.enc.Utf8.parse(JSON.stringify(header));
        let encodedHeader = base64url(stringifiedHeader);
        let stringifiedData = CryptoJS.enc.Utf8.parse(JSON.stringify(payload));
        let encodedData = base64url(stringifiedData);
        return encodedHeader + "." + encodedData;
    }

    function base64url(source) {
        let encodedSource = CryptoJS.enc.Base64.stringify(source);
        while (encodedSource.endsWith('=')) {
            encodedSource = encodedSource.slice(0, -1);
        }
        encodedSource = encodedSource.replace(/\+/g, '-');
        encodedSource = encodedSource.replace(/\//g, '_');
        return encodedSource;
    }

    const handleHomePath = (errMsg) => {
        toast.error(errMsg);
        localStorage.clear();
        window.location.href = "/";
    }

    const handleGetApiResponse = (response) => {
        const errorCodes = [401, 455];
        const resErrorCode = response?.data?.errorCode ?? response?.data?.data?.errorCode;
        const errMsg = response?.data?.message ?? response?.message;

        if (errorCodes.includes(resErrorCode)) {
            toast.error(errMsg);
            localStorage.clear();
            window.location.href = "/";
            return true;
        }

        const errMessage = ["Unauthorized", "Token Expired", "Invalid Token"];
        if (errMessage.includes(errMsg)) {
            handleHomePath("Session expired!")
            return true;
        }
        return false;
    };

    const handlePostApiResponse = (response) => {
        const errorCodes = [401, 455];
        const resErrorCode = response?.data?.errorCode ?? response?.data?.data?.errorCode;
        const errMsg = response?.data?.message ?? response?.message;

        if (errorCodes.includes(resErrorCode)) {
            handleHomePath(errMsg)
            return true;
        }
        if (errMsg === "Unauthorized") {
            handleHomePath("Session expired!")
            return true;
        }
        return false;
    };

    // now async because pyDecrypt is async
    const handleError = async (error, url) => {
        try {
            let errorMessage;
            const useEncDec = shouldEncryptDecrypt(url);
            let decErrorRes = null
            if (useEncDec) {
                decErrorRes = await pyDecrypt(error?.response?.data ?? error?.response?.data?.data)
            } else {
                decErrorRes = error?.response?.data ?? error?.response?.data?.data
            }

            if (error?.response?.status === 401) {
                handleHomePath("Session expired!")
                return
            }
            if (!decErrorRes) {
                toast.error(`Error response not found. (${error?.response?.status ?? error?.message})`)
                return
            }
            if (decErrorRes?.message === "Unauthorized") {
                handleHomePath("Session expired!")
                return
            }

            if (error.response) {
                errorMessage = `Error Code: ${error?.response?.status}\nMessage: ${decErrorRes?.message}`;
            } else if (error.request) {
                errorMessage = "No response received from server.";
            } else {
                errorMessage = `Error: ${decErrorRes?.message}`;
            }
            return toast.error(errorMessage)
        } catch (err) {
            toast.error(err.message || "Network failed Please Try Again...!")
        }
    };

    const doAnotherthing = async () => {
        const datetime = await axios.get(`${REACT_APP_TIME_URL}`);
        return Math.floor(parseFloat(datetime?.data?.data));
    };

    const getCachedDatetime = async () => {
        const currentPathname = window.location.pathname;
        const now = Date.now();

        if (cachedPathname !== currentPathname) {
            cachedPathname = currentPathname;
            cachedDatetime = null;
            cachedDatetimePromise = null;
            lastFetchTime = 0;
        }
        if (cachedDatetime !== null && now - lastFetchTime < REFETCH_SECONDS) {
            return cachedDatetime;
        }
        if (cachedDatetimePromise) {
            return cachedDatetimePromise;
        }

        lastFetchTime = now;
        cachedDatetimePromise = doAnotherthing();

        try {
            cachedDatetime = await cachedDatetimePromise;
            localStorage.setItem('emiTrevres', cachedDatetime.toString());
            return cachedDatetime;
        } finally {
            cachedDatetimePromise = null;
        }
    };

    const getRequest = async (url) => {
        const token = localStorage.getItem("token");
        const useEncDec = shouldEncryptDecrypt(url);

        try {
            await getCachedDatetime()
            const tokenHandling = await getGen(`${REACT_APP_BACKEND_URL}${url}`);
            const headers = {
                "cache-control": "no-cache",
                "content-type": "application/json",
                Authorization: "Bearer " + token,
                "mfhesdwlo-tcbvd": tokenHandling.token,
                "shfyfrjgd-vkwdk": tokenHandling.encrtpted_genT,
            };

            let response;
            if (useEncDec) {
                const decResponse = await axios.get(`${REACT_APP_BACKEND_URL}${url}`, { headers });
                response = await pyDecrypt(decResponse?.data?.data)   // <-- await
                if (!response) {
                    return { message: "Payload could not be decrypt", data: {} }
                }
            } else {
                const decResponse = await axios.get(`${REACT_APP_BACKEND_URL}${url}`, { headers });
                response = decResponse?.data;
            }

            const isBlocked = handleGetApiResponse(response);
            if (isBlocked) return;

            return response;
        } catch (error) {
            return handleError(error, url);
        }
    };

    const postRequest = async (url, requestData) => {
        const token = localStorage.getItem("token");
        const useEncDec = shouldEncryptDecrypt(url);
        const isFormData = requestData instanceof FormData;

        try {
            await getCachedDatetime();

            const tokenHandling = await getGen(`${REACT_APP_BACKEND_URL}${url}`);

            const headers = {
                "cache-control": "no-cache",
                Authorization: "Bearer " + token,
                "mfhesdwlo-tcbvd": tokenHandling.token,
                "shfyfrjgd-vkwdk": tokenHandling.encrtpted_genT,
            };

            const endpoint = `${REACT_APP_BACKEND_URL}${url}`;
            const shouldEncrypt = useEncDec && !isFormData;

            const payload = shouldEncrypt ? { data: await pyEncrypt(requestData) } : requestData; // <-- await

            const apiResponse = await axios.post(endpoint, payload, { headers });

            let response;
            if (useEncDec) {
                response = await pyDecrypt(apiResponse?.data?.data);   // <-- await
                if (!response) {
                    return { message: "Payload could not be decrypt", data: {} };
                }
            } else {
                response = apiResponse?.data;
            }

            const isBlocked = handlePostApiResponse(response);
            if (isBlocked) return;

            return response;
        } catch (error) {
            return handleError(error, url);
        }
    };

    const voiceRequest = async (url, requestData) => {
        try {
            const headers = { "cache-control": "no-cache" };

            const decResponse = await axios.post(
                `http://172.16.16.189:5006${url}`,
                requestData,
                { headers }
            );
            const response = decResponse?.data

            const errorCodes = [401, 455];
            const resErrorCode = response?.data?.errorCode ?? response?.data?.data?.errorCode;
            const errMsg = response?.data?.message ?? response?.message
            if (errorCodes.includes(resErrorCode)) {
                toast.error(errMsg);
                localStorage.clear();
                window.location.href = "/";
                return;
            }
            if (errMsg === "Unauthorized") {
                toast.error("Session expired!");
                localStorage.clear();
                window.location.href = "/";
                return;
            }
            return response;
        } catch (error) {
            return handleError(error, url);
        }
    };

    return { getRequest, postRequest, voiceRequest };
};

export default DataService;